import { deleteCookie, sendRedirect } from "h3";
import { useAuthModule } from "~/stores/auth";

/**
 * Global middleware for handling reset all functionality
 * Runs before other middleware (00 prefix ensures early execution)
 *
 * When query contains 'resetall=true':
 * - Logs out the currently logged-in user
 * - Clears all cache (Pinia stores, localStorage, sessionStorage)
 * - Redirects to /login
 */
export default defineNuxtRouteMiddleware(async (to, _) => {
  // Check if resetall query parameter is present and set to true
  if (to.query.resetall === "true") {
    console.warn("[reset middleware] resetall=true detected on path:", to.path);

    // On server-side, we can't clear browser storage, but we should still
    // clear server-side state and redirect to login
    if (import.meta.server) {
      console.warn("[reset middleware] Server-side: clearing cookies and redirecting to /login");

      // Get the H3 event to delete cookies directly
      const event = useRequestEvent();
      if (event) {
        // Delete the auth cookie directly using H3
        // Must match the cookie options used by pinia-plugin-persistedstate
        deleteCookie(event, "auth", {
          path: "/",
          sameSite: "strict", // matches piniaPersistedstate config
        });
        console.warn("[reset middleware] Deleted auth cookie");

        // Reset auth store on server
        const authStore = useAuthModule();
        authStore.reset();

        // Use H3's sendRedirect directly for immediate redirect with cookie deletion
        await sendRedirect(event, "/login", 302);
        return abortNavigation();
      }

      // Fallback if event is not available
      return navigateTo("/login", { replace: true });
    }

    // Client-side handling
    try {
      console.warn("[reset middleware] Client-side: clearing caches and logging out");
      // Log the reset action
      await AnybillLogger.instance.info("Reset all triggered - clearing all caches and logging out", "reset middleware");

      // Logout clears all caches (Pinia stores, localStorage, sessionStorage, cookies)
      const authStore = useAuthModule();
      authStore.logout();

      // Use window.location for a hard redirect to ensure fresh page state
      // This avoids Vuetify initialization issues from corrupted store state
      window.location.href = "/login";
      return abortNavigation();
    }
    catch (error) {
      console.error("[reset middleware] Error during reset all:", error);

      // Even if there's an error, do a hard redirect to login
      window.location.href = "/login";
      return abortNavigation();
    }
  }
});
